import { OptionType } from "./reactSelectUtils";

export const SMART_SEARCH_SUGGESTION_LIMIT = 200;
export const SEARCH_ALL_DATES_FROM = "1/1/2000";
export const SMART_SEARCH_FIELD_SEPARATOR = "::";

const SMART_SEARCH_FIELD_LABELS: Record<string, string> = {
	Event_ID: "Event ID",
	Event_Name: "Event Name",
	Event_Headliner: "Headliner",
	Event_Headliner_ID: "Headliner ID",
	Event_Time: "Event Time",
	Venue_Name: "Venue",
	Venue_ID: "Venue ID",
	Venue_City: "City",
	Venue_State: "State",
	Country_Code: "Country",
	Cat_Desc: "Category",
	Cat_Type: "Category Type",
	Cat_ID: "Category ID",
	Tags: "Tags",
	Combined_Tags: "Tags",
	Total_Listings: "Listings",
	Total_Tickets: "Tickets",
	Total_Open: "Open",
	Total_Sold: "Sold",
	Total_Reserved: "Reserved",
	Total_Unshared: "Unshared",
	Total_Unpriced: "Unpriced",
	Total_Unpriced_Not_AutoPriced: "Unpriced Not AutoPriced",
	Open_Cost: "Open Cost",
	Total_Cost: "Total Cost",
	Days_Since_Last_Price_Update: "Days Since Last Price Update",
	SeatGeek_Event_ID: "SeatGeek Event ID",
	StubHub_Event_ID: "StubHub Event ID",
	StubHubInt_Event_ID: "StubHub Int Event ID",
	TicketNetwork_Event_ID: "TicketNetwork Event ID",
	TicketsNow_Production_ID: "TicketsNow Production ID",
	Vivid_Production_ID: "Vivid Production ID",
	CIMS_Listing_ID: "CIMS Listing ID",
	Listing_ID: "Listing ID",
	PO_ID: "PO ID",
	Section: "Section",
	Row: "Row",
	Seats: "Seats",
	Quantity: "Quantity",
	Confirmation_No: "Confirmation No",
};

const DATE_FIELD = "Event_Date";

const ZERO_VALUE = /^0+(\.0+)?$/;
const STARTS_WITH_LETTER = /^\p{L}/u;
const STARTS_WITH_DIGIT = /^\p{Nd}/u;

const sortRank = (label: string) => {
	if (STARTS_WITH_LETTER.test(label)) return 0;
	if (STARTS_WITH_DIGIT.test(label)) return 1;
	return 2;
};

export interface SmartSearchEntry {
	value: string;
	fieldLabel: string;
}

const toSearchableText = (value: unknown): string => {
	if (value === null || value === undefined) return "";
	if (typeof value === "string") return value;
	if (typeof value === "number" || typeof value === "bigint" || typeof value === "boolean") return String(value);
	return "";
};

export const getDerivedDateEntries = (record: Record<string, unknown>): Array<SmartSearchEntry> => {
	const raw = toSearchableText(record[DATE_FIELD]);
	if (!raw) return [];

	const parsed = new Date(raw);
	if (isNaN(parsed.getTime())) return [];

	return [
		{ value: parsed.toLocaleDateString("en-US", { weekday: "long" }), fieldLabel: "Weekday" },
		{ value: parsed.toLocaleDateString("en-US", { month: "long" }), fieldLabel: "Month" },
		{
			value: parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
			fieldLabel: "Event Date",
		},
	];
};

export const encodeSmartSearchValue = (fieldLabel: string, term: string) => `${fieldLabel}${SMART_SEARCH_FIELD_SEPARATOR}${term}`;

const DERIVED_DATE_FIELD_LABELS = ["Weekday", "Month", "Event Date"];
const SMART_SEARCH_FIELD_LABEL_SET = new Set([...Object.values(SMART_SEARCH_FIELD_LABELS), ...DERIVED_DATE_FIELD_LABELS]);

export const isSmartSearchFieldLabel = (fieldLabel: string | null | undefined): fieldLabel is string =>
	Boolean(fieldLabel) && SMART_SEARCH_FIELD_LABEL_SET.has(fieldLabel as string);

export const decodeSmartSearchValue = (value: string): { term: string; fieldLabel: string | null } => {
	const separatorIndex = value.indexOf(SMART_SEARCH_FIELD_SEPARATOR);
	if (separatorIndex < 0) return { term: value, fieldLabel: null };

	const fieldLabel = value.slice(0, separatorIndex);
	if (!isSmartSearchFieldLabel(fieldLabel)) return { term: value, fieldLabel: null };

	return { fieldLabel, term: value.slice(separatorIndex + SMART_SEARCH_FIELD_SEPARATOR.length) };
};

const matchesSmartSearchField = (record: Record<string, unknown>, term: string, fieldLabel: string): boolean => {
	const normalizedTerm = term.trim().toLocaleLowerCase();
	const isMatch = (text: string) => text.trim().toLocaleLowerCase() === normalizedTerm;

	for (const [field, value] of Object.entries(record)) {
		if (SMART_SEARCH_FIELD_LABELS[field] === fieldLabel && isMatch(toSearchableText(value))) return true;
	}

	return getDerivedDateEntries(record).some((entry) => entry.fieldLabel === fieldLabel && isMatch(entry.value));
};

export const matchesSmartSearch = <T extends object>(record: T, term: string, fieldLabel?: string | null): boolean => {
	if (!term) return true;
	if (isSmartSearchFieldLabel(fieldLabel)) return matchesSmartSearchField(record as Record<string, unknown>, term, fieldLabel);

	const lowerTerm = term.toLocaleLowerCase();
	const values = Object.values(record);
	for (const value of values) {
		if (toSearchableText(value).toLocaleLowerCase().includes(lowerTerm)) return true;
	}

	const derived = getDerivedDateEntries(record as Record<string, unknown>);
	for (const entry of derived) {
		if (entry.value.toLocaleLowerCase().includes(lowerTerm)) return true;
	}

	return false;
};

export const getSmartSearchOptions = <T extends object>(records: Array<T>): Array<OptionType> => {
	if (!records.length) return [];

	const seen = new Set<string>();
	const options: Array<OptionType> = [];

	const add = (text: string, fieldLabel: string) => {
		const trimmed = text.trim();
		if (!trimmed || ZERO_VALUE.test(trimmed)) return;

		const key = `${fieldLabel}${SMART_SEARCH_FIELD_SEPARATOR}${trimmed.toLocaleLowerCase()}`;
		if (seen.has(key)) return;

		seen.add(key);
		options.push({ label: trimmed, value: encodeSmartSearchValue(fieldLabel, trimmed), fieldLabel });
	};

	records.forEach((record) => {
		const entries = Object.entries(record as Record<string, unknown>);
		entries.forEach(([field, value]) => {
			const fieldLabel = SMART_SEARCH_FIELD_LABELS[field];
			if (!fieldLabel) return;
			add(toSearchableText(value), fieldLabel);
		});

		getDerivedDateEntries(record as Record<string, unknown>).forEach((entry) => add(entry.value, entry.fieldLabel));
	});

	return options.sort((a, b) => {
		const rankDifference = sortRank(a.label) - sortRank(b.label);
		if (rankDifference !== 0) return rankDifference;
		return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" });
	});
};

export const isSmartSearchOption = (options: Array<OptionType>, term: string): boolean => {
	const normalized = term.trim().toLocaleLowerCase();
	return options.some((option) => String(option.label).toLocaleLowerCase() === normalized);
};
